import { consume } from '@lit/context';
import { LitElement } from 'lit';
import { property, state } from 'lit/decorators.js';
import debounce from 'lodash-es/debounce';

import { configuratorGlobal } from '@/context';
import { getNumberValueFromString } from '@/utils';

type Constructor<T> = { new (...args: any[]): T };

export const QuantityInputMixin = <T extends Constructor<LitElement>>(
  superClass: T,
) => {
  class QuantityInputMixinClass extends superClass {
    @property({ type: String })
    optionQueryString = '';

    @property({ type: Number })
    quantity = 0;

    @property({ type: Boolean })
    disabled = false;

    @state()
    data = { quantity: 0 };

    @state()
    firstInteracted = false;

    @state()
    prevCustomQuantity = 0;

    @consume({ context: configuratorGlobal.resetContext, subscribe: true })
    resetConfigurator = false;

    private MAX_QUANTITY_VALUE = 1000000;

    willUpdate(changedProperties: Map<string, any>) {
      if (this.resetConfigurator) {
        this.data = { ...this.data, quantity: 0 };
        configuratorGlobal.updateResetProviderUpdate(this, false);
      }

      if (
        this.quantity === 0 &&
        this.data.quantity > 0 &&
        this.firstInteracted
      ) {
        this.quantity = this.data.quantity;
      }

      if (
        changedProperties.has('quantity') &&
        changedProperties.has('optionQueryString')
      ) {
        // only when both quantity and optionsQueryString are changed at the same time we need to update the quantity
        this.data = {
          ...this.data,
          quantity: this.quantity,
        };
      }
    }

    onChangeQuantity(event: KeyboardEvent) {
      const eventTarget = event.target as HTMLInputElement;
      let quantity = parseFloat(getNumberValueFromString(eventTarget.value));
      if (quantity > this.MAX_QUANTITY_VALUE) {
        eventTarget.value = this.prevCustomQuantity.toString();
        this.setQuantity(this.prevCustomQuantity);
        return;
      }
      this.prevCustomQuantity = isNaN(quantity) ? 0 : quantity;

      if (isNaN(quantity)) {
        quantity = 0;
      }

      this.setQuantity(quantity);
    }

    setQuantity(quantity: number) {
      this.firstInteracted = true;
      this.data = {
        ...this.data,
        quantity,
      };
      this.debounceDispatchSetQuantity();
    }

    debounceDispatchSetQuantity = debounce(
      () =>
        this.dispatchEvent(
          new CustomEvent('quantity-set', {
            composed: true,
            detail: {
              quantity: this.data.quantity,
              queryString: this.optionQueryString + this.data.quantity,
            },
          }),
        ),
      300,
    );

    firstUpdated() {
      if (this.quantity > 0 && !this.firstInteracted) {
        this.data.quantity = this.quantity;
        this.setQuantity(this.quantity);
      }
    }
  }
  return QuantityInputMixinClass;
};
